/*
Custom notifications need to be sent from Client to Server to notify about the event:
suggestion_accepted, suggestion_rejected, suggestion_cancelled

The server knows when event happens:
suggestion_requested, suggestion_loaded, suggestion_not_provided, suggestion_error, suggestion_shown

---------------------------------------------------------------------------------
***************Suggestion tracking*******************************************
suggestion_requested: Sent when the IDE extension requests a suggestion
suggestion_loaded: Sent when the suggestion request came back
suggestion_not_provided: Sent when the suggestion request came back empty
suggestion_error: Sent when the suggestion request leads to an error
suggestion_shown: Sent when the suggestion is started to be shown to the user
suggestion_accepted: Sent when the suggestion was accepted
suggestion_rejected: Sent when the suggestion was rejected
suggestion_cancelled: Sent when the suggestion request was canceled
stream_started: Tracked for generations only when the first chunk of stream was received.
stream_completed: Tracked for generations only when the stream has completed.
---------------------------------------------------------------------------------
 */
export enum TRACKING_EVENTS {
  REQUESTED = 'suggestion_requested',
  LOADED = 'suggestion_loaded',
  NOT_PROVIDED = 'suggestion_not_provided',
  SHOWN = 'suggestion_shown',
  ERRORED = 'suggestion_error',
  ACCEPTED = 'suggestion_accepted',
  REJECTED = 'suggestion_rejected',
  CANCELLED = 'suggestion_cancelled',
  STREAM_STARTED = 'suggestion_stream_started',
  STREAM_COMPLETED = 'suggestion_stream_completed',
}

const END_STATE_NO_TRANSITIONS_ALLOWED: TRACKING_EVENTS[] = [];

const endStatesGraph = new Map<TRACKING_EVENTS, TRACKING_EVENTS[]>([
  [TRACKING_EVENTS.ERRORED, END_STATE_NO_TRANSITIONS_ALLOWED],
  [TRACKING_EVENTS.CANCELLED, END_STATE_NO_TRANSITIONS_ALLOWED],
  [TRACKING_EVENTS.NOT_PROVIDED, END_STATE_NO_TRANSITIONS_ALLOWED],
  [TRACKING_EVENTS.REJECTED, END_STATE_NO_TRANSITIONS_ALLOWED],
  [TRACKING_EVENTS.ACCEPTED, END_STATE_NO_TRANSITIONS_ALLOWED],
]);

export const nonStreamingSuggestionStateGraph = new Map<TRACKING_EVENTS, TRACKING_EVENTS[]>([
  [TRACKING_EVENTS.REQUESTED, [TRACKING_EVENTS.LOADED, TRACKING_EVENTS.ERRORED]],
  [
    TRACKING_EVENTS.LOADED,
    [TRACKING_EVENTS.SHOWN, TRACKING_EVENTS.CANCELLED, TRACKING_EVENTS.NOT_PROVIDED],
  ],
  [TRACKING_EVENTS.SHOWN, [TRACKING_EVENTS.ACCEPTED, TRACKING_EVENTS.REJECTED]],
  ...endStatesGraph,
]);

export const streamingSuggestionStateGraph = new Map<TRACKING_EVENTS, TRACKING_EVENTS[]>([
  [
    TRACKING_EVENTS.REQUESTED,
    [TRACKING_EVENTS.STREAM_STARTED, TRACKING_EVENTS.ERRORED, TRACKING_EVENTS.CANCELLED],
  ],
  [
    TRACKING_EVENTS.STREAM_STARTED,
    [TRACKING_EVENTS.SHOWN, TRACKING_EVENTS.ERRORED, TRACKING_EVENTS.STREAM_COMPLETED],
  ],
  [
    TRACKING_EVENTS.SHOWN,
    [
      TRACKING_EVENTS.ERRORED,
      TRACKING_EVENTS.STREAM_COMPLETED,
      TRACKING_EVENTS.ACCEPTED,
      TRACKING_EVENTS.REJECTED,
    ],
  ],
  [
    TRACKING_EVENTS.STREAM_COMPLETED,
    [TRACKING_EVENTS.ACCEPTED, TRACKING_EVENTS.REJECTED, TRACKING_EVENTS.NOT_PROVIDED],
  ],
  ...endStatesGraph,
]);

export const endStates = [...endStatesGraph].map(([state]) => state);

export const SAAS_INSTANCE_URL = 'https://gitlab.com';
export const TELEMETRY_NOTIFICATION = '$/gitlab/telemetry';
export const CODE_SUGGESTIONS_CATEGORY = 'code_suggestions';

export const GC_TIME = 60000;

export const INSTANCE_TRACKING_EVENTS_MAP = {
  [TRACKING_EVENTS.REQUESTED]: null,
  [TRACKING_EVENTS.LOADED]: null,
  [TRACKING_EVENTS.NOT_PROVIDED]: null,
  [TRACKING_EVENTS.SHOWN]: 'code_suggestion_shown_in_ide',
  [TRACKING_EVENTS.ERRORED]: null,
  [TRACKING_EVENTS.ACCEPTED]: 'code_suggestion_accepted_in_ide',
  [TRACKING_EVENTS.REJECTED]: 'code_suggestion_rejected_in_ide',
  [TRACKING_EVENTS.CANCELLED]: null,
  [TRACKING_EVENTS.STREAM_STARTED]: null,
  [TRACKING_EVENTS.STREAM_COMPLETED]: null,
};

// FIXME: once we remove the default from ConfigService, we can move this back to the SnowplowTracker
export const DEFAULT_TRACKING_ENDPOINT = 'https://snowplow.trx.gitlab.net';

export const TELEMETRY_DISABLED_WARNING_MSG =
  'GitLab Duo Code Suggestions telemetry is disabled. Please consider enabling telemetry to help improve our service.';
export const TELEMETRY_ENABLED_MSG = 'GitLab Duo Code Suggestions telemetry is enabled.';
